import { DimensionDataWithDomain, DomainInfo, PlotAxis } from './plot-data.state'
import { BehaviorSubject, map } from 'rxjs'
import {
    SelectionBase,
    getPointsForSegSource,
    getAllOwnAndChildrenPointsInNode,
    HierarchicalSelectionNode,
} from '../selection/selection.state'
import { denormalizeValue, mergeDomains, normalizeValue } from './plot-data-axis-utils'

const PLOT_DATA_LOG = '[PlotData]'

export type PlotPoint = [number, number, number, number, number, string]

export const plotPointKey = (point: PlotPoint): string => `${point[5]}:${point[0]}`

export const uniquePlotPoints = (points: PlotPoint[]): PlotPoint[] => {
    const seen = new Set<string>()
    const uniquePoints: PlotPoint[] = []
    for (const point of points) {
        const key = plotPointKey(point)
        if (!seen.has(key)) {
            seen.add(key)
            uniquePoints.push(point)
        }
    }
    return uniquePoints
}

export interface PlotData {
    domainX: DomainInfo | null
    domainY: DomainInfo | null
    domainColor: DomainInfo | null
    points: PlotPoint[]
    legend: { key: number; value: string }[] | null
}

interface InternalPlotData {
    domainX: DomainInfo | null
    domainY: DomainInfo | null
    domainColor: DomainInfo | null
    points: PlotPoint[]
    /** `${segSource}:${segmentId}` keys for points selectable at the current hierarchy level. */
    pointsThisLevel: string[] | null
    /** `${segSource}:${segmentId}` keys for points already sent to descendant levels. */
    pointsLowerLevels: string[] | null
    pointType: 0 | 1 | 2
    legend: { key: number; value: string }[] | null
}

let _plotData: InternalPlotData = {
    domainX: null,
    domainY: null,
    domainColor: null,
    points: [],
    pointsThisLevel: null,
    pointsLowerLevels: null,
    pointType: 0,
    legend: null,
}

const _internalPlotData$ = new BehaviorSubject<InternalPlotData>(_plotData)

function toDisplayPlotData(data: InternalPlotData): PlotData {
    if (!data.domainColor || data.pointType === 0)
        return {
            domainX: data.domainX,
            domainY: data.domainY,
            domainColor: data.domainColor,
            points: data.points,
            legend: data.legend,
        }

    const pointsToShowRegularly = data.pointsThisLevel ? new Set(data.pointsThisLevel) : null
    const displayPoints = [
        ...data.points.map(point => [
            point[0],
            point[1],
            point[2],
            point[3],
            pointsToShowRegularly && pointsToShowRegularly.has(plotPointKey(point)) ? 0 : 1,
        ]),
    ]
    return {
        domainX: data.domainX,
        domainY: data.domainY,
        domainColor: data.domainColor,
        points: displayPoints as PlotPoint[],
        legend: data.legend,
    }
}

export const plotData$ = _internalPlotData$.pipe(map(toDisplayPlotData))

export const getDisplayPlotData = (): PlotData => toDisplayPlotData(_plotData)

export const hasDisplayPlotData = (): boolean =>
    !!_plotData.domainX && !!_plotData.domainY && _plotData.points.length > 0

export const plotFilterData$ = _internalPlotData$.pipe(
    map((data: InternalPlotData): Set<string> | null => {
        // Selectable points for the scatter plot as `${segSource}:${segmentId}` keys.
        if (data.pointType == 0) {
            // Do not filter in free selection mode
            return null
        }
        if (data.pointsThisLevel) return new Set(data.pointsThisLevel)
        if (data.pointsLowerLevels) {
            const pointsLowerLevels = new Set(data.pointsLowerLevels)
            const selectablePoints = data.points
                .map(point => plotPointKey(point))
                .filter(key => !pointsLowerLevels.has(key))
            return new Set(selectablePoints)
        }
        return null
    })
)

const setPlotData = (data: InternalPlotData): void => {
    _plotData = data
    _internalPlotData$.next(_plotData)
}

export const resetPlotData = (): void => {
    setPlotData({
        domainX: null,
        domainY: null,
        domainColor: null,
        points: [],
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: null,
    })
}

export const setPlotDataType = (type: 0 | 1 | 2): void => {
    setPlotData({
        domainX: _plotData.domainX,
        domainY: _plotData.domainY,
        domainColor: _plotData.domainColor,
        points: _plotData.points,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: type,
        legend: _plotData.legend,
    })
}

export const setPlotDataForBothAxes = (
    dataX: DimensionDataWithDomain,
    dataY: DimensionDataWithDomain,
    segSource: string = ''
): void => {
    // Join by segment ID — property bins are not guaranteed to share index order.
    // Preserve color channel (membership index or property color) across X/Y reloads.
    const prevColorByKey = new Map(
        _plotData.points.map(p => [plotPointKey(p), p[3]] as const)
    )
    const yById = new Map(dataY.points.map(([id, value]) => [id, value]))
    const points: PlotPoint[] = []
    for (const [id, xVal] of dataX.points) {
        const yVal = yById.get(id)
        if (yVal === undefined) continue
        const color = prevColorByKey.get(plotPointKey([id, 0, 0, 0, 0, segSource])) ?? 0
        points.push([id, xVal, yVal, color, 0, segSource])
    }
    if (points.length === 0) {
        throw new Error('Data size mismatch for X / Y axes, discarding previous data')
    }
    console.log(PLOT_DATA_LOG, 'setPlotDataForBothAxes', {
        segSource,
        pointCount: points.length,
        xPoints: dataX.points.length,
        yPoints: dataY.points.length,
        domainX: dataX.domain,
        domainY: dataY.domain,
    })
    setPlotData({
        domainX: dataX.domain,
        domainY: dataY.domain,
        domainColor: _plotData.domainColor,
        points,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: _plotData.legend,
    })
}

export const setPlotDataForMultiMapping = (
    entries: {
        dataX: DimensionDataWithDomain
        dataY: DimensionDataWithDomain
        segSource: string
        mapName?: string
        mapId?: number
    }[],
    options?: { colorByMapping?: boolean }
): void => {
    if (entries.length === 0) {
        console.log(PLOT_DATA_LOG, 'setPlotDataForMultiMapping → reset (no entries)')
        resetPlotData()
        return
    }

    console.log(PLOT_DATA_LOG, 'setPlotDataForMultiMapping started', {
        mapCount: entries.length,
        colorByMapping: !!options?.colorByMapping,
        perMapDomains: entries.map(e => ({
            mapName: e.mapName,
            mapId: e.mapId,
            segSource: e.segSource,
            domainX: e.dataX.domain,
            domainY: e.dataY.domain,
        })),
    })

    const domainX = mergeDomains(entries.map(entry => entry.dataX.domain))
    const domainY = mergeDomains(entries.map(entry => entry.dataY.domain))
    const mapCount = entries.length
    const mapNames = entries.map(e => e.mapName ?? e.segSource)
    const mappingDomain = {
        lowerBound: 0,
        upperBound: Math.max(mapCount - 1, 0),
    }
    const colorByMapping = !!options?.colorByMapping
    console.log(PLOT_DATA_LOG, 'setPlotDataForMultiMapping → merged shared domains', {
        domainX,
        domainY,
    })

    // Keep membership / prior color indices when not coloring by intensity map.
    const prevColorByKey = colorByMapping
        ? null
        : new Map(_plotData.points.map(p => [plotPointKey(p), p[3]] as const))

    const points: PlotPoint[] = []

    entries.forEach((entry, mapIndex) => {
        const { dataX, dataY, segSource, mapName, mapId } = entry
        const yById = new Map(dataY.points.map(([id, value]) => [id, value]))
        const bySeg = new Map<string, PlotPoint>()
        let joined = 0
        dataX.points.forEach(e => {
            const id = e[0]
            const yVal = yById.get(id)
            if (yVal === undefined) return
            joined++
            const key = plotPointKey([id, 0, 0, 0, 0, segSource])
            if (!bySeg.has(key)) {
                const physicalX = denormalizeValue(e[1], dataX.domain)
                const physicalY = denormalizeValue(yVal, dataY.domain)
                const colorNorm = colorByMapping
                    ? normalizeValue(mapIndex, mappingDomain)
                    : (prevColorByKey?.get(key) ?? 0)
                bySeg.set(key, [
                    id,
                    normalizeValue(physicalX, domainX),
                    normalizeValue(physicalY, domainY),
                    colorNorm,
                    0,
                    segSource,
                ])
            }
        })
        if (joined === 0) {
            console.warn(PLOT_DATA_LOG, 'setPlotDataForMultiMapping → skip map (no matching X/Y segment IDs)', {
                mapName,
                mapId,
                xLength: dataX.points.length,
                yLength: dataY.points.length,
            })
            return
        }
        const mapPoints = [...bySeg.values()]
        console.log(PLOT_DATA_LOG, 'setPlotDataForMultiMapping → denormalized & re-normalized map', {
            mapName: mapName ?? segSource,
            mapId,
            segSource,
            sourceDomainX: dataX.domain,
            sourceDomainY: dataY.domain,
            pointCount: mapPoints.length,
        })
        points.push(...mapPoints)
    })

    const mergedPoints = uniquePlotPoints(points)
    console.log(PLOT_DATA_LOG, 'setPlotDataForMultiMapping → merge complete', {
        rawPointCount: points.length,
        uniquePointCount: mergedPoints.length,
        domainX,
        domainY,
    })

    setPlotData({
        domainX,
        domainY,
        // When coloring by intensity map, set categorical map index domain; otherwise leave
        // domainColor null so membership colors (or a later property color load) own point[3].
        domainColor: colorByMapping ? mappingDomain : null,
        points: mergedPoints,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: colorByMapping
            ? mapNames.map((value, key) => ({ key, value }))
            : null,
    })
}

/** Apply a shared color property across multi-map plot points, keyed by segSource + segment id. */
export const setPlotDataColorForMultiMapping = (
    entries: { data: DimensionDataWithDomain; segSource: string }[]
): void => {
    if (entries.length === 0) {
        throw new Error('No color data loaded for multi-map color axis')
    }
    const domainColor = mergeDomains(entries.map(e => e.data.domain))
    const colorByKey = new Map<string, number>()
    for (const { data, segSource } of entries) {
        for (const [id, value] of data.points) {
            const physical = denormalizeValue(value, data.domain)
            colorByKey.set(`${segSource}:${id}`, normalizeValue(physical, domainColor))
        }
    }
    const points: PlotPoint[] = []
    for (const existing of _plotData.points) {
        const color = colorByKey.get(plotPointKey(existing))
        if (color === undefined) continue
        points.push([existing[0], existing[1], existing[2], color, 0, existing[5]])
    }
    if (points.length === 0) {
        throw new Error('Data size mismatch for color axis across intensity maps')
    }
    const legend = entries.find(e => e.data.legend)?.data.legend ?? null
    setPlotData({
        domainX: _plotData.domainX,
        domainY: _plotData.domainY,
        domainColor,
        points,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend,
    })
}

export const setPlotDataForAxis = (data: DimensionDataWithDomain | null, axis: PlotAxis): void => {
    // incoming data format [segment_id, value]
    // plot data format [segment_id, x_value, y_value, color_value]
    if (!data) {
        resetAxisKeepOthers(axis)
        return
    }
    const resetOtherAxis =
        (axis === PlotAxis.X && !_plotData.domainY) || (axis === PlotAxis.Y && !_plotData.domainX)
    resetOtherAxis
        ? setPlotDataForOneAxisResetOther(data, axis)
        : setPlotDataForOneAxisKeepOther(data, axis)
}

export const filterPlotData = (
    pointsThisLevel: Set<string> | null,
    pointsLowerLevels: Set<string> | null
): void => {
    setPlotData({
        domainX: _plotData.domainX,
        domainY: _plotData.domainY,
        domainColor: _plotData.domainColor,
        points: _plotData.points,
        pointsThisLevel: pointsThisLevel ? Array.from(pointsThisLevel.values()) : null,
        pointsLowerLevels: pointsLowerLevels ? Array.from(pointsLowerLevels.values()) : null,
        pointType: _plotData.pointType,
        legend: _plotData.legend,
    })
}

export const setMultipleInstanceData = (selections: SelectionBase[]): void => {
    // Scatter membership coloring is driven only by displayed selections (multi-select),
    // not by layer eye toggles. Hierarchical levels still use own ∪ descendants with
    // deeper selections overwriting earlier ones (same representation as Neuroglancer).
    //
    // Precompute membership once per (selection, segSource), then assign colors in one
    // pass over points. Nested per-point subtree walks were O(points × selections × tree).
    const points = _plotData.points
    if (selections.length === 0) {
        for (const p of points) {
            p[3] = 0
        }
        setPlotData({ ..._plotData, points })
        return
    }

    const segSources = new Set<string>()
    for (const p of points) {
        segSources.add(p[5])
    }

    // segSource -> segmentId -> colorIndex (later selections overwrite = child wins)
    const colorBySegSource = new Map<string, Map<number, number>>()
    for (const segSource of segSources) {
        colorBySegSource.set(segSource, new Map())
    }

    selections.forEach((selection, i) => {
        const colorIndex = i + 2
        for (const segSource of segSources) {
            const members = membershipPointsForSelection(selection, segSource)
            if (members.size === 0) continue
            const idMap = colorBySegSource.get(segSource)!
            for (const id of members) {
                idMap.set(id, colorIndex)
            }
        }
    })

    for (const p of points) {
        p[3] = colorBySegSource.get(p[5])?.get(p[0]) ?? 0
    }

    setPlotData({ ..._plotData, points })
}

/** Own buckets for free/leaf selections; own ∪ descendants for hierarchical nodes. */
const membershipPointsForSelection = (
    selection: SelectionBase,
    segSource: string
): Set<number> => {
    if ('children' in selection) {
        return getAllOwnAndChildrenPointsInNode(
            selection as HierarchicalSelectionNode,
            segSource
        )
    }
    return getPointsForSegSource(selection, segSource)
}

export const getPlotData = (): InternalPlotData => {
    return _plotData
}

const setPlotDataForOneAxisResetOther = (data: DimensionDataWithDomain, axis: PlotAxis): void => {
    setPlotData({
        domainX: axis === PlotAxis.X ? data.domain : null,
        domainY: axis === PlotAxis.Y ? data.domain : null,
        domainColor: axis === PlotAxis.COLOR ? data.domain : null,
        points: data.points.map(
            (point: [number, number]): PlotPoint => {
                return axis === PlotAxis.X
                    ? [point[0], point[1], 0, 0, 0, '']
                    : [point[0], 0, point[1], 0, 0, '']
            }
        ),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? data.legend! : null,
    })
}

const setPlotDataForOneAxisKeepOther = (data: DimensionDataWithDomain, axis: PlotAxis): void => {
    // Join by segment ID — property bins are not guaranteed to share index order.
    const valueById = new Map(data.points.map(([id, value]) => [id, value]))
    const points: PlotPoint[] = []
    for (const existing of _plotData.points) {
        const id = existing[0]
        const newVal = valueById.get(id)
        if (newVal === undefined) continue
        const segSource = existing[5]
        if (axis === PlotAxis.X) {
            points.push([id, newVal, existing[2], existing[3], 0, segSource])
        } else if (axis === PlotAxis.Y) {
            points.push([id, existing[1], newVal, existing[3], 0, segSource])
        } else {
            points.push([id, existing[1], existing[2], newVal, 0, segSource])
        }
    }
    if (points.length === 0) {
        throw new Error('Data size mismatch for X / Y axes, discarding previous data')
    }
    setPlotData({
        domainX: axis === PlotAxis.X ? data.domain : _plotData.domainX,
        domainY: axis === PlotAxis.Y ? data.domain : _plotData.domainY,
        domainColor: axis === PlotAxis.COLOR ? data.domain : _plotData.domainColor,
        points,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? data.legend! : _plotData.legend,
    })
}

const resetAxisKeepOthers = (axis: PlotAxis): void => {
    setPlotData({
        domainX: axis === PlotAxis.X ? null : _plotData.domainX,
        domainY: axis === PlotAxis.Y ? null : _plotData.domainY,
        domainColor: axis === PlotAxis.COLOR ? null : _plotData.domainColor,
        points: _plotData.points.map(
            (point: PlotPoint): PlotPoint => {
                const segSource = point[5]
                if (axis === PlotAxis.X) {
                    return [point[0], 0, point[2], point[3], 0, segSource]
                } else if (axis === PlotAxis.Y) {
                    return [point[0], point[1], 0, point[3], 0, segSource]
                } else {
                    return [point[0], point[1], point[2], 0, 0, segSource]
                }
            }
        ),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? null : _plotData.legend,
    })
}
